import { ComponentProps, memo } from 'react';

import {
  MarkdownContentSize,
  getMarkdownComponents,
} from '@backrs_ui/common-ui/src/molecules/Markdown/markdownComponents';

import 'katex/dist/katex.min.css';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

type RemarkPlugins = ComponentProps<typeof ReactMarkdown>['remarkPlugins'];
type RehypePlugins = ComponentProps<typeof ReactMarkdown>['rehypePlugins'];

/**
 * Extends the default schema rather than replacing it — a `u`-only allowlist would strip the `<p>` and `<table>` markdown produces.
 */
const questionSchema: typeof defaultSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), 'u'],
  attributes: {
    ...defaultSchema.attributes,
    code: [['className', /^language-./, 'math-inline', 'math-display']],
  },
};

/**
 * Math is `$$…$$` only, so a single `$` stays literal — question text is full of prices, and the
 * default would swallow everything between two of them.
 */
export const questionRemarkPlugins: RemarkPlugins = [
  remarkGfm,
  [remarkMath, { singleDollarTextMath: false }],
];

/**
 * Order matters both ways: raw HTML must become elements before the sanitizer can judge it, and the
 * sanitizer must run before KaTeX emits markup it would otherwise strip.
 */
export const questionRehypePlugins: RehypePlugins = [
  rehypeRaw,
  [rehypeSanitize, questionSchema],
  rehypeKatex,
];

/** Question copy is markdown; Science passages include GFM tables and Math items inline LaTeX. */
export const QuestionMarkdown = memo(
  ({ content, size }: Readonly<{ content: string; size: MarkdownContentSize }>) => (
    <ReactMarkdown
      remarkPlugins={questionRemarkPlugins}
      rehypePlugins={questionRehypePlugins}
      components={getMarkdownComponents(size)}
    >
      {content}
    </ReactMarkdown>
  ),
);

// Zero the first/last block's outer margin so markdown sits flush with its container.
const markdownFlowSx = {
  '& > :first-child': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },
};

// Not a design token: question copy is set in the tests' own serif so it reads like the real booklet.
const TEST_SERIF = "'Times New Roman', Times, serif";

// index.css sets `* { font-family: … !important }`, which hits every descendant directly — so the
// serif has to be declared per element and out-specify it. Math is excluded so the serif's metrics
// don't stretch KaTeX's glyph boxes.
export const testSerifSx = {
  '&, & :not(.katex, .katex *)': { fontFamily: `${TEST_SERIF} !important` },
};

export const markdownContentSx = {
  ...markdownFlowSx,
  ...testSerifSx,
};
